import { useCallback, useEffect, useMemo } from 'react'

import { SkipBlockerResponse } from '@cash-web/protos/squareup/franklin/app/blocker.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { useMutationCompleteScenario } from '@cash-web/shared-data-access-api/react-query/useMutationCompleteScenario'
import { useMutationGetFlow } from '@cash-web/shared-data-access-api/react-query/useMutationGetFlow'
import { useMutationSkipBlocker } from '@cash-web/shared-data-access-api/react-query/useMutationSkipBlocker'
import {
  FlowContextProps,
  FlowData,
  FlowEvents,
  FlowInitialProps,
  FlowStartMethods,
  FlowStartProps,
  FlowUpdateMethods,
  FlowUpdateProps,
  getBlockerDataDetail,
  isTerminalBlocker,
  SkipBlocker,
} from '@cash-web/shared-flow-data-access-context'
import { OptionalMiddlewareContextProps } from '@cash-web/shared-util-fetch'

import { useLogBlockerErrorEvent } from '../../../util'
import { getPaymentTokens, getTransferToken } from '../middleware/response'
import { getActiveBlocker, getFlowType, isFlowComplete } from '../utils'
import { FlowCompatExternal, FlowStateExternal } from './flowReducer'
import { useBlockerAction } from './useBlockerAction'
import { useFlowListeners } from './useFlowListeners'
import { useFlowLogging } from './useFlowLogging'
import { useFlowOptionalMiddleware } from './useFlowOptionalMiddleware'
import { useFlowReducer } from './useFlowReducer'
import { useFlowResponseHandler } from './useFlowResponseHandler'
import { useLoadUrl } from './useLoadUrl'

export type UseFlowResponse = {
  logBlockerError: () => void
  optionalMiddleware: OptionalMiddlewareContextProps
} & FlowContextProps &
  FlowStateExternal &
  FlowCompatExternal

export type UseFlowProps = {
  initialFlow?: FlowInitialProps
} & FlowEvents

export const useFlow = ({
  initialFlow,
  onCompleteFlow,
  onCancelFlow,
  onBlockerView,
}: UseFlowProps = {}): UseFlowResponse => {
  const flowListeners = useFlowListeners({ initialFlow })

  const { state, dispatch } = useFlowReducer({
    initialState: { ...initialFlow },
    onCompleteFlow: async () => {
      // Notify children we have completed the flow.
      await flowListeners.onCompleteFlow()
      onCompleteFlow?.()
    },
    onCancelFlow: async (override?: () => void) => {
      // notify children we have canceled the flow
      await flowListeners.onCancelFlow()
      // allows us to override the global onCancelFlow,
      // while still notifying listeners of cancel
      const cancelFlow = override ?? onCancelFlow

      cancelFlow?.()
    },
    // if the flow is trying to be canceled, and we have a dismiss action
    // this event will be triggered.
    onDismissAction: (blockerAction: BlockerAction) => {
      executeBlockerAction(blockerAction)
    },
    onBlockerView: async () => {
      onBlockerView?.()
    },
  })

  // does a lot of the heavy lifting for the blocker updating
  const optionalMiddleware = useFlowOptionalMiddleware({
    state,
    dispatch,
  })

  const { logBlockerSkip } = useFlowLogging()

  const activeBlockerDescriptor = useMemo(
    () => getActiveBlocker(state.skippedBlockers, state.blockerDescriptors),
    [state.skippedBlockers, state.blockerDescriptors]
  )

  // get the blocker data
  const blockerData = useMemo(() => getBlockerDataDetail(activeBlockerDescriptor ?? {}), [activeBlockerDescriptor])

  const { mutateAsync: completeScenario } = useMutationCompleteScenario()
  const { mutateAsync: getFlow } = useMutationGetFlow({ options: { flowToken: state.flowToken } })

  const handleFlowResponse = useFlowResponseHandler(state, dispatch)
  // used to call getFlow or completeScenario based on the flow data, and handle extracting the response
  const getFlowData = useCallback(
    async (config: FlowData) => {
      // try to convert FlowDataWithFlowOnly
      if ('flow' in config) {
        const {
          flow: { initiationData, flowType, requestContext },
          ...rest
        } = config

        const response = getFlow({
          ...(initiationData ? { initiation_data: initiationData } : {}),
          ...(flowType ? { flow_type: flowType } : {}),
          ...(requestContext ? { request_context: requestContext } : {}),
        })

        // convert to a FlowDataWithResponseOnly
        config = {
          ...rest,
          response,
        }
      }

      // FlowDataWithScenarioOnly - we have a clientScenario and no blockers or response
      if (!('response' in config) && 'clientScenario' in config && !('blockerDescriptors' in config)) {
        const request = config.paymentTokens != null ? { payment_tokens: config.paymentTokens } : {}

        const response = completeScenario({
          ...request,
          options: { clientScenario: config.clientScenario, flowToken: config.flowToken ?? state.flowToken },
        })

        // convert to a FlowDataWithResponseOnly
        config = {
          ...config,
          // it's expected that we change our client scenario to match the scenario we want to complete.
          clientScenario: config.clientScenario,
          response,
        }
      }

      // try to convert FlowDataWithResponseOnly
      if ('response' in config) {
        const { response, ...flowProps } = config

        const result = await handleFlowResponse(response)
        config = {
          clientScenario: result.client_scenario ?? config.clientScenario,
          blockerDescriptors: result.response_context?.scenario_plan?.blocker_descriptors ?? [],
          flowToken: result.token ?? result.flow_token ?? config.flowToken ?? state.flowToken,
          responseContext: result.response_context,
          paymentTokens: getPaymentTokens(result),
          transferToken: getTransferToken(result),
          ...flowProps,
        }
      }

      // finally FlowDataWithBlockersOnly
      return config
    },
    [completeScenario, getFlow, handleFlowResponse, state.flowToken]
  )
  const reset = useCallback(() => {
    flowListeners.reset()
    dispatch({ type: 'FLOW_RESET' })
  }, [dispatch, flowListeners])

  const restore = useCallback(
    (id: string) => {
      if (state.suspended[id] != null) {
        dispatch({ type: 'FLOW_RESTORE', id })
        flowListeners.restore(id)
      }
    },
    [dispatch, flowListeners, state]
  )

  const suspend = useCallback(
    (id: string) => {
      dispatch({ type: 'FLOW_SUSPEND', id })
      flowListeners.suspend(id)
    },
    [dispatch, flowListeners]
  )

  const startFlow = useCallback(
    async (flow: FlowStartProps) => {
      if (flow.trackingFlowName === undefined) {
        throw new Error('Flow Start Error: No trackingFlowName provided')
      }

      if (flow.trackingFlowName === state.trackingFlowName) {
        // TODO: Solve the below issue: https://block.atlassian.net/browse/FSWEB-11130
        // logger.warn(new Error(`Blocker flow already started ${state.trackingFlowName}`))
        return
      }

      dispatch({ type: 'FLOW_SET_NAME', name: flow.trackingFlowName })

      const data = await getFlowData(flow)

      flowListeners.add(flow)
      // FlowDataWithBlockersOnly - default
      dispatch({
        type: 'FLOW_START',
        ...data,
        trackingFlowName: flow.trackingFlowName,
        flowType: getFlowType(flow),
      })
      // if we have no blockers, we should complete the flow
      if (isFlowComplete(data)) {
        dispatch({ type: 'FLOW_COMPLETE' })
      }

      return Promise.resolve()
    },
    [dispatch, flowListeners, getFlowData, state.trackingFlowName]
  )
  const startMethods: FlowStartMethods = useMemo(
    () => ({
      startWithBlockerDescriptors: startFlow,
      startWithClientScenario: startFlow,
      startWithResponse: startFlow,
      startWithFlow: startFlow,
    }),
    [startFlow]
  )

  const updateFlow = useCallback(
    async (flow: FlowUpdateProps) => {
      if (state.trackingFlowName == null || !state.isInProgress) {
        throw new Error(`Flow Update Error: Cannot update a flow that has not started`)
      }
      const data = await getFlowData(flow)

      flowListeners.add(flow)
      dispatch({ type: 'FLOW_UPDATE', ...data })

      // if we have no blockers, we should complete the flow
      if (isFlowComplete(data)) {
        dispatch({ type: 'FLOW_COMPLETE' })
      }
    },
    [dispatch, flowListeners, getFlowData, state.isInProgress, state.trackingFlowName]
  )

  const updateMethods: FlowUpdateMethods = useMemo(
    () => ({
      updateWithBlockerDescriptors: updateFlow,
      updateWithClientScenario: updateFlow,
      updateWithResponse: updateFlow,
      updateWithFlow: updateFlow,
    }),
    [updateFlow]
  )

  const complete = useCallback(() => {
    dispatch({ type: 'FLOW_COMPLETE' })
  }, [dispatch])

  const cancel = useCallback(
    ({ onCancelFlowOverride, force }: { onCancelFlowOverride?: () => void; force?: boolean } = {}) => {
      dispatch({ type: 'FLOW_CANCEL', force, onCancelFlowOverride })
    },
    [dispatch]
  )

  useEffect(() => {
    // if we changed blockers it's now null log viewed
    if (activeBlockerDescriptor !== state.activeBlockerDescriptor && activeBlockerDescriptor != null) {
      dispatch({ type: 'BLOCKER_VIEW', blockerDescriptor: activeBlockerDescriptor })
    }
  }, [dispatch, activeBlockerDescriptor, state.activeBlockerDescriptor])

  // LOGGING
  const logBlockerErrorEvent = useLogBlockerErrorEvent()
  // error

  const logBlockerError = useCallback(() => {
    logBlockerErrorEvent({
      clientScenario: state.clientScenario,
      blockerName: blockerData.trackingName,
      blockerDescriptorId: blockerData.id,
      flowToken: state.flowToken,
      flowName: state.trackingFlowName,
      terminalBlocker: isTerminalBlocker(state.responseContext?.scenario_plan),
    })
  }, [
    logBlockerErrorEvent,
    state.clientScenario,
    state.flowToken,
    state.trackingFlowName,
    state.responseContext?.scenario_plan,
    blockerData.trackingName,
    blockerData.id,
  ])

  const { mutateAsync: remoteSkip } = useMutationSkipBlocker({ options: { optionalMiddleware } })

  const skipBlocker: SkipBlocker = useCallback(
    async (forceRemote = false) => {
      if (blockerData.type == null || blockerData.blocker == null) {
        throw new Error('No blocker to skip')
      }

      logBlockerSkip(state)

      if (blockerData.remoteSkip || forceRemote) {
        dispatch({ type: 'BLOCKER_SKIP_REMOTE' })
        const response = await remoteSkip({
          blockers: { [blockerData.type]: blockerData.blocker },
        })
        await updateFlow({
          response: Promise.resolve(response),
          queriesToInvalidate: [],
        })
        return response
      } else {
        dispatch({ type: 'BLOCKER_SKIP_LOCAL' })

        if (state.blockerDescriptors.length === 1) {
          complete()
        }
      }

      return Promise.resolve({ status: 'SUCCESS' } as SkipBlockerResponse)
    },
    [
      blockerData.blocker,
      blockerData.remoteSkip,
      blockerData.type,
      complete,
      dispatch,
      logBlockerSkip,
      remoteSkip,
      state,
      updateFlow,
    ]
  )

  const loadUrl = useLoadUrl({ state, dispatch })
  const executeBlockerAction = useBlockerAction({
    state,
    dispatch,
    skipBlocker,
    loadUrl,
    completeFlow: complete,
    cancelFlow: cancel,
    updateFlow,
    optionalMiddleware,
  })

  const closeBlockerAction = useCallback(() => {
    dispatch({ type: 'BLOCKER_ACTION_CLOSE' })
  }, [dispatch])

  const active = useMemo(
    () => ({
      blockerData,
      paymentTokens: state.paymentTokens,
      transferToken: state.transferToken,
      statusResult: state.responseContext?.status_result,
      blockerActionView: state.blockerActionView,
      trackingFlowName: state.trackingFlowName,
      clientScenario: state.clientScenario,
      flowToken: state.flowToken,
    }),
    [
      blockerData,
      state.paymentTokens,
      state.transferToken,
      state.responseContext?.status_result,
      state.blockerActionView,
      state.trackingFlowName,
      state.clientScenario,
      state.flowToken,
    ]
  )

  // removing state from useFlowResponse
  const {
    blockerSubmitStartTime,
    blockerViewStartTime,
    flowStartTime,
    trackingFlowName,
    skippedBlockers,
    isInProgress,
    isLoading,
    error,
    ...contextData
  } = state

  return useMemo(
    () => ({
      error,
      isInProgress,
      isLoading,
      active,
      // FlowCompatExternal properties moved to main level
      activeBlockerDescriptor,
      ...contextData,
      logBlockerError,
      ...startMethods,
      ...updateMethods,
      complete,
      cancel,
      reset,
      restore,
      suspend,
      skipBlocker,
      optionalMiddleware,
      loadUrl,
      executeBlockerAction,
      closeBlockerAction,
    }),
    [
      error,
      isInProgress,
      isLoading,
      active,
      activeBlockerDescriptor,
      contextData,
      logBlockerError,
      startMethods,
      updateMethods,
      complete,
      cancel,
      reset,
      restore,
      suspend,
      skipBlocker,
      optionalMiddleware,
      loadUrl,
      executeBlockerAction,
      closeBlockerAction,
    ]
  )
}
